"use client";
import { createAvatar } from "@gluestack-ui/core/avatar/creator";
import React, { ComponentProps } from "react";

import { Platform, Text, View } from "react-native";

import type { VariantProps } from "@gluestack-ui/utils/nativewind-utils";
import {
  tva,
  useStyleContext,
  withStyleContext,
} from "@gluestack-ui/utils/nativewind-utils";
import { Image } from "expo-image";
import {
  buildResizedImageUri,
  isResizableImageUri,
  stableImageCacheKey,
} from "../image/responsiveSource";
import { cssInterop } from "nativewind";

const SCOPE = "AVATAR";

const UIAvatar = createAvatar({
  Root: withStyleContext(View, SCOPE),
  Badge: View,
  Group: View,
  Image: Image,
  FallbackText: Text,
});

cssInterop(UIAvatar, { className: "style" });
cssInterop(UIAvatar.Badge, { className: "style" });
cssInterop(UIAvatar.Group, { className: "style" });
cssInterop(UIAvatar.Image, { className: "style" });
cssInterop(UIAvatar.FallbackText, { className: "style" });

const avatarStyle = tva({
  base: "justify-center items-center relative bg-primary-600 group-[.avatar-group]/avatar-group:-ml-2.5 overflow-hidden",
  variants: {
    size: {
      xs: "w-6 h-6",
      sm: "w-8 h-8",
      md: "w-12 h-12",
      lg: "w-16 h-16",
      xl: "w-24 h-24",
      "2xl": "w-32 h-32",
    },
    rounded: {
      none: "rounded-none",
      sm: "rounded-sm",
      md: "rounded-md",
      lg: "rounded-lg",
      xl: "rounded-xl",
      full: "rounded-full",
    },
    action: {
      primary: "bg-primary-600",
      transparent: "bg-transparent",
      brand: "bg-brand-200",
    },
  },
});

const avatarFallbackTextStyle = tva({
  base: "text-typography-0 font-semibold overflow-hidden text-transform:uppercase web:cursor-default",

  parentVariants: {
    size: {
      xs: "text-2xs",
      sm: "text-xs",
      md: "text-base",
      lg: "text-xl",
      xl: "text-3xl",
      "2xl": "text-5xl",
    },
  },
});

const avatarGroupStyle = tva({
  base: "group/avatar-group flex-row-reverse relative avatar-group",
});

const avatarBadgeStyle = tva({
  base: "w-5 h-5 bg-success-500 rounded-full absolute right-0 bottom-0 border-background-0 border-2",
  parentVariants: {
    size: {
      xs: "w-2 h-2",
      sm: "w-2 h-2",
      md: "w-3 h-3",
      lg: "w-4 h-4",
      xl: "w-6 h-6",
      "2xl": "w-8 h-8",
    },
  },
});

const avatarImageStyle = tva({
  base: "h-full w-full absolute top-0 left-0",
});

type IAvatarProps = Omit<
  React.ComponentPropsWithoutRef<typeof UIAvatar>,
  "context"
> &
  VariantProps<typeof avatarStyle>;

export const Avatar = React.forwardRef<
  React.ComponentRef<typeof UIAvatar>,
  IAvatarProps
>(
  (
    { className, size = "md", rounded = "full", action = "primary", ...props },
    ref,
  ) => {
    return (
      <UIAvatar
        ref={ref}
        {...props}
        className={avatarStyle({ size, rounded, action, class: className })}
        context={{ size }}
      />
    );
  },
);

Avatar.displayName = "Avatar";

type IAvatarBadgeProps = React.ComponentPropsWithoutRef<typeof UIAvatar.Badge> &
  VariantProps<typeof avatarBadgeStyle>;

export const AvatarBadge = React.forwardRef<
  React.ComponentRef<typeof UIAvatar.Badge>,
  IAvatarBadgeProps
>(({ className, size, ...props }, ref) => {
  const { size: parentSize } = useStyleContext(SCOPE);

  return (
    <UIAvatar.Badge
      ref={ref}
      {...props}
      className={avatarBadgeStyle({
        parentVariants: {
          size: parentSize,
        },
        size,
        class: className,
      })}
    />
  );
});

AvatarBadge.displayName = "AvatarBadge";

type IAvatarFallbackTextProps = React.ComponentPropsWithoutRef<
  typeof UIAvatar.FallbackText
> &
  VariantProps<typeof avatarFallbackTextStyle>;
export const AvatarFallbackText = React.forwardRef<
  React.ComponentRef<typeof UIAvatar.FallbackText>,
  IAvatarFallbackTextProps
>(({ className, size, ...props }, ref) => {
  const { size: parentSize } = useStyleContext(SCOPE);

  return (
    <UIAvatar.FallbackText
      ref={ref}
      {...props}
      className={avatarFallbackTextStyle({
        parentVariants: {
          size: parentSize,
        },
        size,
        class: className,
      })}
    />
  );
});

AvatarFallbackText.displayName = "AvatarFallbackText";

type IAvatarImageProps = React.ComponentPropsWithoutRef<typeof UIAvatar.Image> &
  VariantProps<typeof avatarImageStyle>;

const transformSource = (
  source: ComponentProps<typeof AvatarImage>["source"],
  size: ComponentProps<typeof Avatar>["size"],
) => {
  if (typeof source === "number") return source;
  if (typeof source === "string") return source;
  if (Array.isArray(source)) return source;
  if (!source) return { uri: undefined };
  if (typeof source !== "object" || !("uri" in source))
    return { uri: undefined };
  if (typeof source.uri !== "string" || !source.uri) return { uri: undefined };
  // Only the media CDN resizes. Local/inline sources (picked files, base64 logos)
  // and other hosts are used as they are: a resize query would corrupt the first
  // and mean nothing to the second.
  if (!isResizableImageUri(source.uri)) return source;
  const { uri, ...rest } = source;
  const resized = buildResizedImageUri(
    uri,
    AVATAR_WIDTHS[size ?? "lg"] ?? 128,
    "auto",
  );
  return { uri: resized, cacheKey: stableImageCacheKey(resized), ...rest };
};

const AVATAR_WIDTHS: Record<
  NonNullable<ComponentProps<typeof Avatar>["size"]>,
  number
> = {
  xs: 48,
  sm: 64,
  md: 96,
  lg: 128,
  xl: 192,
  "2xl": 256,
};

export const AvatarImage = React.forwardRef<
  React.ComponentRef<typeof UIAvatar.Image>,
  IAvatarImageProps
>(({ className, source, style, contentFit, ...props }, ref) => {
  const { size: parentSize } = useStyleContext(SCOPE);
  const newSource = transformSource(source, parentSize);
  return (
    <UIAvatar.Image
      ref={ref}
      contentFit={contentFit ?? "cover"}
      {...props}
      className={avatarImageStyle({
        class: className,
      })}
      source={newSource}
      // @ts-ignore
      style={
        Platform.OS === "web"
          ? { height: "revert-layer", width: "revert-layer" }
          : style
      }
    />
  );
});

AvatarImage.displayName = "AvatarImage";

type IAvatarGroupProps = React.ComponentPropsWithoutRef<typeof UIAvatar.Group> &
  VariantProps<typeof avatarGroupStyle>;

export const AvatarGroup = React.forwardRef<
  React.ComponentRef<typeof UIAvatar.Group>,
  IAvatarGroupProps
>(({ className, ...props }, ref) => {
  return (
    <UIAvatar.Group
      ref={ref}
      {...props}
      className={avatarGroupStyle({
        class: className,
      })}
    />
  );
});

AvatarGroup.displayName = "AvatarGroup";
